import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import type { Appearance, StripeElementsOptions } from "@stripe/stripe-js";
import { getStripe } from "@/lib/stripe";
import { useCreateSetupIntent } from "@/api/hooks/customer.hooks";

// Theme the Stripe iframe to the UberDoc brand tokens (see src/index.css).
// Stripe appearance variables take CSS color strings; comma-form hsl() is the
// most broadly compatible.
const appearance: Appearance = {
  theme: "stripe",
  variables: {
    colorPrimary: "hsl(225, 100%, 59%)",
    colorText: "hsl(236, 92%, 10%)",
    colorDanger: "hsl(0, 84.2%, 60.2%)",
    colorBackground: "hsl(0, 0%, 100%)",
    borderRadius: "0.625rem",
    fontFamily: "Outfit, Inter, system-ui, sans-serif",
    spacingUnit: "4px",
  },
};

export type CardCollectorHandle = {
  /**
   * Confirm the SetupIntent, saving the entered card to the Stripe customer.
   * Resolves with the outcome so callers can gate follow-up work (e.g. creating
   * an appointment) on a successfully saved card.
   */
  confirm: () => Promise<{ ok: true } | { ok: false; error: string }>;
};

type CardFieldsProps = {
  onReady?: () => void;
};

// Lives inside <Elements> so it can access the Stripe + Elements instances.
const CardFields = forwardRef<CardCollectorHandle, CardFieldsProps>(
  function CardFields({ onReady }, ref) {
    const stripe = useStripe();
    const elements = useElements();

    useImperativeHandle(
      ref,
      () => ({
        async confirm() {
          if (!stripe || !elements) {
            return { ok: false, error: "Payment form is still loading." };
          }
          const submit = await elements.submit();
          if (submit.error) {
            return {
              ok: false,
              error: submit.error.message ?? "Please check your card details.",
            };
          }
          const { error } = await stripe.confirmSetup({
            elements,
            redirect: "if_required",
          });
          if (error) {
            return {
              ok: false,
              error: error.message ?? "We couldn't save your card.",
            };
          }
          return { ok: true };
        },
      }),
      [stripe, elements],
    );

    return (
      <PaymentElement
        onReady={onReady}
        options={{ layout: "tabs", fields: { billingDetails: "auto" } }}
      />
    );
  },
);

type CardCollectorProps = {
  /** Called once a SetupIntent client secret is available and Elements mounts. */
  onReady?: () => void;
  /** Called if the SetupIntent could not be created. */
  onError?: (message: string) => void;
};

/**
 * Fetches a save-only SetupIntent, mounts Stripe Elements, and exposes an
 * imperative `confirm()` so a parent form can save the card as part of its own
 * submit sequence. Reused by the booking form and the profile page.
 */
export const CardCollector = forwardRef<CardCollectorHandle, CardCollectorProps>(
  function CardCollector({ onReady, onError }, ref) {
    const createSetupIntent = useCreateSetupIntent();
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    // Load Stripe.js only when the card form actually mounts (not at import),
    // so pages that merely import this component don't pull in js.stripe.com.
    const [stripePromise] = useState(getStripe);
    const fieldsRef = useRef<CardCollectorHandle>(null);
    const requested = useRef(false);

    useImperativeHandle(
      ref,
      () => ({
        confirm: () =>
          fieldsRef.current?.confirm() ??
          Promise.resolve({
            ok: false as const,
            error: "Payment form is still loading.",
          }),
      }),
      [],
    );

    // A SetupIntent is created once per mount. React 18 StrictMode double-mounts
    // effects in dev, so `requested` guards against a duplicate intent.
    useEffect(() => {
      if (requested.current) return;
      requested.current = true;
      createSetupIntent.mutate(undefined, {
        onSuccess: (res) => setClientSecret(res.clientSecret),
        onError: () =>
          onError?.("We couldn't start card setup. Please try again."),
      });
    }, [createSetupIntent, onError]);

    if (createSetupIntent.isError) {
      return (
        <p className="text-sm text-destructive">
          We couldn't load the card form. Please refresh and try again.
        </p>
      );
    }

    if (!clientSecret) {
      return (
        <div
          className="h-24 rounded-[0.625rem] bg-muted animate-pulse"
          aria-label="Loading payment form"
        />
      );
    }

    const options: StripeElementsOptions = { clientSecret, appearance };

    return (
      <Elements stripe={stripePromise} options={options}>
        <CardFields ref={fieldsRef} onReady={onReady} />
      </Elements>
    );
  },
);
